import { urlApi } from './api'
import { base, ecrireReglage, lireReglage, remplacerZonage } from './db'
import { emettre, surEvenement } from './bus'
import { horlogeSysteme, type Horloge } from './horloge'
import {
  etatFile,
  lotAEnvoyer,
  mettreEnQuarantaine,
  reporter,
  retirer,
  reveiller,
} from './file'
import { uuid } from './uuid'
import type { EvenementFile, SecteurZonage } from '../domaine/types'

/*
  Client de synchronisation.

  Rien ici n'est sur le chemin d'une saisie : l'interface n'attend jamais une
  réponse de ce module. Au pire, la file grossit et le bandeau l'affiche.

  Jamais de message d'erreur à l'écran. Hors réseau n'est pas une panne, c'est
  l'état normal d'une plage.
*/

export interface ReponseSync {
  acceptes: string[]
  rejetes: { id: string; raison: string }[]
  zonage_version: number
  serveur_ts: string
}

export interface ReponseZonage {
  version: number
  secteurs: SecteurZonage[]
}

export type ResultatEnvoi = 'vide' | 'ok' | 'echec' | 'hors-ligne' | 'non-authentifie'



/** Le navigateur se croit parfois en ligne ; il ne se croit jamais hors ligne à tort. */
function probablementEnLigne(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

async function blobEnBase64(blob: Blob): Promise<string> {
  const lecteur = new FileReader()
  return new Promise((resoudre, rejeter) => {
    lecteur.onerror = () => rejeter(lecteur.error)
    lecteur.onload = () => {
      const resultat = String(lecteur.result)
      // data:image/jpeg;base64,XXXX → on ne garde que XXXX
      resoudre(resultat.slice(resultat.indexOf(',') + 1))
    }
    lecteur.readAsDataURL(blob)
  })
}

/**
 * Complète un événement avant envoi.
 *
 * Les photos ne sont pas stockées en base64 dans la file : ce serait doubler
 * leur poids sur l'appareil. Le blob vit dans le magasin `photos`, on l'encode
 * seulement au moment de partir.
 */
async function corpsEvenement(e: EvenementFile): Promise<Record<string, unknown> | null> {
  if (e.op !== 'photo.televerser') return e.donnees

  const db = await base()
  const photo = await db.get('photos', e.id)
  if (!photo) return null // photo effacée entre-temps : l'événement n'a plus d'objet

  return { ...e.donnees, base64: await blobEnBase64(photo.blob) }
}

/** Un tour d'envoi. Ne lève jamais : renvoie toujours un résultat exploitable. */
export async function envoyerUnLot(horloge: Horloge = horlogeSysteme): Promise<ResultatEnvoi> {
  const jeton = await lireReglage('device_token')
  if (!jeton) return 'non-authentifie'

  const lot = await lotAEnvoyer(horloge)
  if (lot.length === 0) return 'vide'

  if (!probablementEnLigne()) return 'hors-ligne'

  const evenements: Array<{ id: string; op: string; donnees: Record<string, unknown> }> = []
  const orphelins: string[] = []

  for (const e of lot) {
    const donnees = await corpsEvenement(e)
    if (donnees === null) {
      orphelins.push(e.id)
      continue
    }
    evenements.push({ id: e.id, op: e.op, donnees })
  }
  await retirer(orphelins)
  if (evenements.length === 0) return 'vide'

  let reponse: Response
  try {
    reponse = await fetch(urlApi('sync'), {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-Device-Token': jeton },
      body: JSON.stringify({ lot_id: uuid(), evenements }),
    })
  } catch {
    // Coupure réseau, DNS, timeout : on réessaiera. Rien à afficher.
    await reporter(
      lot.map((e) => e.id),
      horloge,
    )
    return 'echec'
  }

  if (reponse.status === 401) {
    emettre('reseau-change')
    return 'non-authentifie'
  }

  if (!reponse.ok) {
    await reporter(
      lot.map((e) => e.id),
      horloge,
    )
    return 'echec'
  }

  const resultat = (await reponse.json()) as ReponseSync

  // Un événement déjà présent en base revient dans `acceptes`, pas dans
  // `rejetes` : c'est ce qui rend le renvoi sûr. Le client purge sa file sans
  // avoir à distinguer « inséré » de « déjà là ».
  await retirer(resultat.acceptes ?? [])

  for (const r of resultat.rejetes ?? []) {
    await mettreEnQuarantaine(r.id, r.raison)
  }

  // Ce qui n'a été ni accepté ni rejeté sera repris au tour suivant.
  const traites = new Set([
    ...(resultat.acceptes ?? []),
    ...(resultat.rejetes ?? []).map((r) => r.id),
  ])
  await reporter(
    lot.filter((e) => !traites.has(e.id)).map((e) => e.id),
    horloge,
  )

  await ecrireReglage('dernier_envoi_ok', resultat.serveur_ts ?? horloge.iso())

  const versionLocale = await lireReglage('zonage_version')
  if (typeof resultat.zonage_version === 'number' && resultat.zonage_version !== versionLocale) {
    // En arrière-plan, jamais sur le chemin d'une saisie.
    void rafraichirZonage()
  }

  emettre('file-changee')
  return 'ok'
}

/** Vide la file autant que possible, en enchaînant les lots. */
export async function vider(horloge: Horloge = horlogeSysteme): Promise<void> {
  for (let tour = 0; tour < 50; tour++) {
    const r = await envoyerUnLot(horloge)
    if (r !== 'ok') return
  }
}

export async function rafraichirZonage(): Promise<void> {
  const jeton = await lireReglage('device_token')
  if (!jeton || !probablementEnLigne()) return
  try {
    const r = await fetch(urlApi('zonage'), {
      cache: 'no-store',
      headers: { 'X-Device-Token': jeton },
    })
    if (!r.ok) return
    const z = (await r.json()) as ReponseZonage
    await remplacerZonage(z.secteurs, z.version)
    emettre('zonage-change')
  } catch {
    // Le zonage local reste valable. Rien à signaler.
  }
}

/* ── Ordonnanceur ─────────────────────────────────────────────────── */

let minuteur: ReturnType<typeof setTimeout> | null = null
let enCours = false

async function tour(): Promise<void> {
  if (enCours) return
  enCours = true
  try {
    await vider()
  } finally {
    enCours = false
    await replanifier()
  }
}

async function replanifier(): Promise<void> {
  if (minuteur) clearTimeout(minuteur)
  minuteur = null

  const etat = await etatFile()
  if (etat.prochaineEcheance === null) return

  const delai = Math.max(500, etat.prochaineEcheance - Date.now())
  minuteur = setTimeout(() => void tour(), delai)
}

/** Démarre l'envoi opportuniste. Idempotent. */
export function demarrerSynchronisation(): void {
  if (typeof window === 'undefined') return

  /*
   * Une nouvelle saisie doit réveiller l'ordonnanceur.
   *
   * Sans cet abonnement, `replanifier()` ne pose aucun minuteur quand la file
   * est vide — et rien ne repart quand elle se remplit ensuite. Une patrouille
   * entière restait alors sur le téléphone jusqu'au prochain événement
   * `online` ou retour d'arrière-plan, alors que le réseau était disponible.
   */
  surEvenement('file-changee', () => {
    if (!enCours) void replanifier()
  })

  window.addEventListener('online', () => {
    emettre('reseau-change')
    // Retour de connectivité : on réarme tout le backoff immédiatement.
    void reveiller().then(() => tour())
  })
  window.addEventListener('offline', () => emettre('reseau-change'))

  // Revenir sur l'application est un aussi bon signal que `online`.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void tour()
  })

  void tour()
}

export function arreterSynchronisation(): void {
  if (minuteur) clearTimeout(minuteur)
  minuteur = null
}
